/**
 * School vocabulary: each school chooses how it names its learners, its year, its classes and its
 * sections (a university has "étudiants" in "niveaux", a language school "apprenants" in
 * "groupes"…). The texts are written with the default words (élève, année scolaire, classe,
 * section / student, academic year, class, section) and rewritten here, in French and English,
 * with the articles and agreements that change with the gender of the new word.
 *
 * The rules run on the translation templates, never on the final text: names and other values
 * inserted into {{placeholders}} are left as they are. Keep in sync with
 * backend/src/Shared/I18n/Vocabulary.php (same rules, same tests).
 */
/* eslint-disable no-control-regex -- \u0001 and \u0002 mark replaced words while rules run */

export type LearnerTerm = 'eleve' | 'etudiant' | 'apprenant'
export type YearTerm = 'school' | 'university' | 'session'
export type ClassTerm = 'class' | 'level' | 'programme'
export type SectionTerm = 'section' | 'group'

export interface Vocabulary {
  learner: LearnerTerm
  year: YearTerm
  class: ClassTerm
  section: SectionTerm
}

export const DEFAULT_VOCABULARY: Vocabulary = {
  learner: 'eleve',
  year: 'school',
  class: 'class',
  section: 'section',
}

export function isDefaultVocabulary(v: Vocabulary): boolean {
  return (Object.keys(DEFAULT_VOCABULARY) as (keyof Vocabulary)[]).every(
    (k) => v[k] === DEFAULT_VOCABULARY[k],
  )
}

const L = '\\p{L}'
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)
const withCase = (source: string, word: string) =>
  source.charAt(0) === source.charAt(0).toUpperCase() ? cap(word) : word

/** A replacement that is never touched again (the new word keeps its case, gender and number). */
interface Noun {
  word: string
  masculine: boolean
}

// Feminine determiners and adjectives placed before a noun that becomes masculine.
const FR_DETERMINERS: [string, string][] = [
  ['l’une des', 'l’un des'],
  ["l'une des", "l'un des"],
  ['aucune autre', 'aucun autre'],
  ['une autre', 'un autre'],
  ['une seule', 'un seul'],
  ['la même', 'le même'],
  ['toutes les', 'tous les'],
  ['toute la', 'tout le'],
  ['de la', 'du'],
  ['à la', 'au'],
  ['la', 'le'],
  ['une', 'un'],
  ['cette', 'ce'],
  ['aucune', 'aucun'],
  ['chaque', 'chaque'],
  ['sa', 'son'],
  ['ma', 'mon'],
  ['ta', 'ton'],
  ['quelle', 'quel'],
  ['nouvelle', 'nouveau'],
  ['nouvelles', 'nouveaux'],
  ['seule', 'seul'],
  ['première', 'premier'],
  ['dernière', 'dernier'],
  ['toutes', 'tous'],
]
// Feminine adjectives after the noun (participles ending in -ée(s) are handled generally).
const FR_ADJECTIVES: [string, string][] = [
  ['supérieure', 'supérieur'],
  ['actuelle', 'actuel'],
  ['pleine', 'plein'],
  ['complète', 'complet'],
  ['ouverte', 'ouvert'],
  ['principale', 'principal'],
  ['seule', 'seul'],
  ['inscrite', 'inscrit'],
  ['mise', 'mis'],
  ['prise', 'pris'],
  ['suivante', 'suivant'],
  ['précédente', 'précédent'],
  ['courante', 'courant'],
  ['existante', 'existant'],
  ['inconnue', 'inconnu'],
  ['confondue', 'confondu'],
  ['active', 'actif'],
  ['inactive', 'inactif'],
]
// Words allowed between a noun and its adjective ("La classe a été supprimée").
const FR_BRIDGE = new Set([
  'a',
  'est',
  'été',
  'sont',
  'ont',
  'pas',
  'bien',
  'déjà',
  'encore',
  'plus',
  'n’est',
  "n'est",
  'n’a',
  "n'a",
  'ne',
  'sera',
  'seront',
  'était',
  'et',
  'peut',
  'peuvent',
  'être',
  'doit',
  'doivent',
])

/** Rewrites the determiner before a marker and the agreeing adjectives after it (masculine nouns). */
function frAgreement(text: string, nouns: Noun[]): string {
  let out = text
  nouns.forEach((noun, i) => {
    if (!noun.masculine) return
    const marker = `\u0001${i}\u0001`
    for (const [fem, masc] of FR_DETERMINERS) {
      const re = new RegExp(`(?<![${L}])(${fem})( ${marker})`, 'giu')
      out = out.replace(re, (_m, found: string, rest: string) => withCase(found, masc) + rest)
    }
    // Adjectives and participles in the few words after the noun (stops at punctuation and prepositions).
    const at = out.indexOf(marker)
    if (at < 0) return
    const tail = out.slice(at + marker.length)
    const words = tail.split(/(\s+)/)
    let seen = 0
    for (let w = 0; w < words.length && seen < 5; w++) {
      const token = words[w] ?? ''
      if (/^\s+$/.test(token) || token === '') continue
      if (/[\u0001.,;:!?()«»"]/.test(token)) {
        const clean = token.replace(/[.,;:!?()«»"]+$/, '')
        const fixed = frMasculine(clean)
        if (fixed !== clean) words[w] = fixed + token.slice(clean.length)
        break
      }
      const fixed = frMasculine(token)
      if (fixed !== token) {
        words[w] = fixed
        seen++
        continue
      }
      if (FR_BRIDGE.has(token.toLowerCase())) continue
      break
    }
    out = out.slice(0, at + marker.length) + words.join('')
  })
  return out
}

function frMasculine(word: string): string {
  for (const [fem, masc] of FR_ADJECTIVES) {
    if (word.toLowerCase() === fem) return withCase(word, masc)
    if (word.toLowerCase() === fem + 's')
      return withCase(word, masc === 'principal' ? 'principaux' : masc + 's')
  }
  if (/^\p{L}+ées$/u.test(word)) return word.slice(0, -3) + 'és'
  if (/^\p{L}+ée$/u.test(word)) return word.slice(0, -2) + 'é'
  return word
}

function frRewrite(text: string, v: Vocabulary): string {
  const nouns: Noun[] = []
  const mark = (word: string, masculine: boolean) => {
    nouns.push({ word, masculine })
    return `\u0001${nouns.length - 1}\u0001`
  }
  let out = text

  // Class rooms keep their name ("salon de classe").
  out = out.replace(/salons? de classe/giu, (m) => mark(m, false))

  if (v.learner !== 'eleve') {
    const [sg, pl] = v.learner === 'etudiant' ? ['étudiant', 'étudiants'] : ['apprenant', 'apprenants']
    out = out.replace(
      new RegExp(`(?<![${L}])(une )?([Éé])lève(s?)(?![${L}])`, 'gu'),
      (_m, une: string | undefined, e: string, s: string) =>
        (une ? 'un ' : '') + mark(e === 'É' ? cap(s ? pl : sg) : s ? pl : sg, false),
    )
  }

  if (v.year === 'university') {
    out = out.replace(/(?<![\p{L}])([Aa]nnées?) scolaires?(?![\p{L}])/gu, (_m, annee: string) =>
      mark(annee + (annee.endsWith('s') ? ' universitaires' : ' universitaire'), false),
    )
  } else if (v.year === 'session') {
    out = out
      .replace(
        /(?<![\p{L}])([Ll])[’']année scolaire(?![\p{L}])/gu,
        (_m, l: string) => (l === 'L' ? 'La ' : 'la ') + mark('session', false),
      )
      .replace(
        /(?<![\p{L}])([Dd])[’']année scolaire(?![\p{L}])/gu,
        (_m, d: string) => (d === 'D' ? 'De ' : 'de ') + mark('session', false),
      )
      .replace(
        /(?<![\p{L}])([Ll])[’']année en cours(?![\p{L}])/gu,
        (_m, l: string) => (l === 'L' ? 'La ' : 'la ') + mark('session', false) + ' en cours',
      )
      .replace(
        /(?<![\p{L}])([Dd])[’']année en cours(?![\p{L}])/gu,
        (_m, d: string) => (d === 'D' ? 'De ' : 'de ') + mark('session', false) + ' en cours',
      )
      .replace(
        /(?<![\p{L}])([Aa])nnée en cours(?![\p{L}])/gu,
        (_m, a: string) => mark(a === 'A' ? 'Session' : 'session', false) + ' en cours',
      )
      .replace(/(?<![\p{L}])([Aa])nnées scolaires(?![\p{L}])/gu, (_m, a: string) =>
        mark(a === 'A' ? 'Sessions' : 'sessions', false),
      )
      .replace(/(?<![\p{L}])([Aa])nnée scolaire(?![\p{L}])/gu, (_m, a: string) =>
        mark(a === 'A' ? 'Session' : 'session', false),
      )
  }

  if (v.class !== 'class') {
    const [sg, pl, masculine] =
      v.class === 'level' ? ['niveau', 'niveaux', true] : ['filière', 'filières', false]
    out = out.replace(new RegExp(`(?<![${L}])([Cc])lasse(s?)(?![${L}])`, 'gu'), (_m, c: string, s: string) =>
      mark(c === 'C' ? cap(s ? pl : sg) : s ? pl : sg, masculine),
    )
  }

  if (v.section !== 'section') {
    out = out.replace(new RegExp(`(?<![${L}])([Ss])ection(s?)(?![${L}])`, 'gu'), (_m, c: string, s: string) =>
      mark(c === 'S' ? (s ? 'Groupes' : 'Groupe') : s ? 'groupes' : 'groupe', true),
    )
  }

  out = frAgreement(out, nouns)
  return out.replace(/\u0001(\d+)\u0001/g, (_m, i: string) => nouns[Number(i)]?.word ?? '')
}

function enRewrite(text: string, v: Vocabulary): string {
  const kept: string[] = []
  const keep = (m: string) => {
    kept.push(m)
    return `\u0002${kept.length - 1}\u0002`
  }
  let out = text
  // Fixed phrases: class teacher (the role) and class rooms (the messaging feature).
  out = out.replace(/\bclass teachers?\b|\bclass rooms?\b/gi, keep)
  const word = (re: RegExp, sg: string, pl: string) => {
    out = out.replace(re, (m: string, s?: string) => withCase(m, s ? pl : sg))
  }
  if (v.learner === 'apprenant') word(/\bstudent(s?)\b/gi, 'learner', 'learners')
  if (v.year === 'session') {
    out = out.replace(/\b(an?) (academic|school) year\b/gi, (_m, a: string) => withCase(a, 'a') + ' session')
    word(/\b(?:academic|school) year(s?)\b/gi, 'session', 'sessions')
  } else if (v.year === 'university') {
    out = out.replace(/\bschool year(s?)\b/gi, (m: string, s: string) => withCase(m, 'academic year' + s))
  }
  if (v.class === 'level') word(/\bclass(es)?\b/gi, 'level', 'levels')
  if (v.class === 'programme') word(/\bclass(es)?\b/gi, 'programme', 'programmes')
  if (v.section === 'group') word(/\bsection(s?)\b/gi, 'group', 'groups')
  return out.replace(/\u0002(\d+)\u0002/g, (_m, i: string) => kept[Number(i)] ?? '')
}

/** Rewrites one translation template, leaving {{placeholders}} untouched. */
export function applyVocabulary(template: string, lang: string, v: Vocabulary): string {
  if (isDefaultVocabulary(v) || template === '') return template
  const rewrite = lang.startsWith('fr') ? frRewrite : enRewrite
  return template
    .split(/({{[^}]*}}|<[^>]+>)/)
    .map((part, i) => (i % 2 === 1 ? part : rewrite(part, v)))
    .join('')
}
